import React, { useEffect, useState, useMemo, useCallback } from "react";

const BrowserGuard = ({ children }) => {
    const [isBlocked, setIsBlocked] = useState(false);
    const ua = useMemo(() => navigator.userAgent || "", []);
    const url = useMemo(() => window.location.href, []);

    const isAndroid = useMemo(() => /Android/i.test(ua), [ua]);
    const isIOS = useMemo(() => /iPhone|iPad|iPod/i.test(ua), [ua]);

    // 🕵️ EXTRA-SENSITIVE DETECTION
    const isInApp = useMemo(() => {
        const isInstagram = /Instagram/i.test(ua);
        const isWhatsApp = /WhatsApp/i.test(ua);
        const isFacebook = /FBAN|FBAV|FB_IAB|FB4A|FB_AK/i.test(ua);
        const isMessenger = /Messenger/i.test(ua);
        const isOtherInApp = /Line|Snapchat|MicroMessenger|Pinterest|Twitter|Zalo|WebView|(; wv\))|Android.*wv/i.test(ua);

        // On iOS, "Version/" is present in Safari. 
        // We only want to block if "Version/" is present AND it's not standard Safari (identified by the absence of other specific in-app strings)
        // However, standard in-app detection usually relies on specific app strings.
        const isIOSWebView = isIOS && /Version\/[0-9\.]+/i.test(ua) && !/Safari/i.test(ua);
        const isAndroidWebView = isAndroid && /Version\/[0-9\.]+/i.test(ua);

        return isInstagram || isWhatsApp || isFacebook || isMessenger || isOtherInApp || isIOSWebView || isAndroidWebView;
    }, [ua, isIOS, isAndroid]);

    // 🚀 REDIRECTION LOGIC
    const forceChromeLaunch = useCallback(() => {
        if (!isAndroid) return;

        const rawUrl = url.replace(/^https?:\/\//, "");
        const intent = `intent://${rawUrl}#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(url)};end;`;

        window.location.replace(intent);
        window.location.href = intent;
    }, [isAndroid, url]);

    const forceIosLaunch = useCallback((browser) => {
        if (!isIOS) return;

        const rawUrl = url.replace(/^https?:\/\//, "");
        let schemeUrl = url;

        if (browser === 'chrome') {
            schemeUrl = `googlechromes://${rawUrl}`;
        } else if (browser === 'firefox') {
            schemeUrl = `firefox://open-url?url=${url}`;
        }

        window.location.href = schemeUrl;

        // Fallback for Safari if it's the target
        if (browser === 'safari') {
            const a = document.createElement("a");
            a.href = url;
            a.target = "_blank";
            a.rel = "noopener noreferrer";
            a.click();
        }
    }, [isIOS, url]);

    useEffect(() => {
        if (isInApp) {
            setIsBlocked(true);
            if (isAndroid) {
                forceChromeLaunch();
                const t1 = setTimeout(forceChromeLaunch, 500);
                const t2 = setTimeout(forceChromeLaunch, 1500);
                return () => {
                    clearTimeout(t1);
                    clearTimeout(t2);
                };
            }
        }
    }, [isInApp, isAndroid, forceChromeLaunch]);

    const handleManualOpen = () => {
        if (isAndroid) {
            forceChromeLaunch();
        } else {
            navigator.clipboard.writeText(url);
            alert("Link copied! Now open Safari.");
        }
    };

    // 3. UI RENDERING
    if (!isInApp) return children;

    return (
        <div style={styles.container}>
            <style>{animations}</style>

            <div style={styles.card}>
                <div style={styles.iconWrapper}>
                    <div style={styles.pulse}></div>
                    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#A3102F" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                        <polyline points="15 3 21 3 21 9"></polyline>
                        <line x1="10" y1="14" x2="21" y2="3"></line>
                    </svg>
                </div>

                <h2 style={styles.title}>Switching Browser...</h2>
                <p style={styles.description}>
                    {isAndroid
                        ? "We are automatically redirecting you to Chrome for the best experience."
                        : "Instagram/WhatsApp browsers are limited. Launch a full browser to continue."}
                </p>

                <div style={styles.buttonWrapper}>
                    {isAndroid ? (
                        <button style={styles.primaryButton} onClick={forceChromeLaunch}>
                            🚀 Launch Chrome Now
                        </button>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <button style={styles.primaryButton} onClick={() => forceIosLaunch('chrome')}>
                                🚀 Launch Chrome
                            </button>
                            <button style={{ ...styles.primaryButton, background: '#007AFF' }} onClick={() => forceIosLaunch('safari')}>
                                🧭 Open in Safari
                            </button>
                            <button
                                style={{ ...styles.primaryButton, background: 'transparent', color: '#64748b', border: '1px solid #e2e8f0', boxShadow: 'none', padding: '12px' }}
                                onClick={handleManualOpen}
                            >
                                📋 Copy Link (Fallback)
                            </button>
                        </div>
                    )}
                </div>

                <div style={styles.infoBox}>
                    <p style={styles.infoText}>
                        <b>Browsing within apps is restricted.</b>
                        <br />
                        Switching to a dedicated browser ensures your media is processed at full quality.
                    </p>
                </div>
            </div>
        </div>
    );
};

const animations = `
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes pulse { 0% { transform: scale(0.95); opacity: 0.5; } 50% { transform: scale(1.1); opacity: 0.2; } 100% { transform: scale(0.95); opacity: 0.5; } }
`;

const styles = {
    container: { height: "100vh", display: "flex", justifyContent: "center", alignItems: "center", background: "#f8fafc", padding: "24px", fontFamily: "'Outfit', sans-serif" },
    card: { background: "white", padding: "40px 24px", borderRadius: "32px", boxShadow: "0 25px 60px rgba(0, 0, 0, 0.05)", width: "100%", maxWidth: "420px", textAlign: "center", animation: "fadeIn 0.6s ease-out", border: "1px solid #f1f5f9" },
    iconWrapper: { position: "relative", width: "100px", height: "100px", background: "#fef2f2", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 32px" },
    pulse: { position: "absolute", width: "100%", height: "100%", background: "#fef2f2", borderRadius: "50%", animation: "pulse 2s infinite" },
    title: { fontSize: "28px", fontWeight: "800", color: "#1e293b", margin: "0 0 16px 0", letterSpacing: "-1px" },
    description: { fontSize: "15px", color: "#64748b", lineHeight: "1.6", margin: "0 0 32px 0" },
    buttonWrapper: { position: "relative", zIndex: 10 },
    primaryButton: { background: "#A3102F", color: "white", border: "none", width: "100%", padding: "20px", borderRadius: "20px", fontSize: "17px", fontWeight: "700", cursor: "pointer", boxShadow: "0 12px 24px rgba(163, 16, 47, 0.25)", transition: "all 0.2s" },
    infoBox: { marginTop: "40px", padding: "20px", background: "#f8fafc", borderRadius: "24px", border: "1px solid #f1f5f9", textAlign: "left" },
    infoText: { fontSize: "13px", color: "#64748b", margin: 0, lineHeight: "1.7" }
};

export default BrowserGuard;
